import { datadogRum } from "@datadog/browser-rum";
import { isDefiniteChunkLoadError } from "src/lib/monitoring";
import {
  currentBuildSha,
  extractAssetUrl,
  overwriteCachedAsset,
  stringifyError,
} from "src/lib/chunk-reload";

type RetryStage = "skew-probe" | "cache-reload" | "plain-retry" | "ts-param";
type RetryOutcome = "recovered" | "failed" | "skew-detected";

/**
 * lazyNamed's missing-export error deliberately mimics the Chrome chunk-load
 * message so it fingerprints with chunk errors in Sentry. Mark it structurally
 * so the retry ladder never wastes network attempts on it.
 */
export function markMissingExport(error: Error): Error {
  (error as Error & { missingExport?: boolean }).missingExport = true;
  return error;
}

export function isMissingExportError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error as Error & { missingExport?: boolean }).missingExport === true
  );
}

/**
 * Mark a chunk-load error as deploy skew: the probe proved the asset is gone
 * from the current deploy (404) or is being served as SPA HTML. The error
 * boundaries key off this structural marker to take a single URL-preserving
 * reload to the fresh build, distinct from the SHA-keyed poison/network budget.
 */
export function markDeploySkew<T>(error: T): T {
  if (error instanceof Error) {
    (error as Error & { deploySkew?: boolean }).deploySkew = true;
  }
  return error;
}

export function isDeploySkewError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error as Error & { deploySkew?: boolean }).deploySkew === true
  );
}

function reportRetry(
  stage: RetryStage,
  outcome: RetryOutcome,
  url: string | null,
  error: unknown,
): void {
  datadogRum.addAction("chunk:retry", {
    stage,
    outcome,
    url,
    build_sha: currentBuildSha(),
    message: stringifyError(error),
  });
}

/**
 * A 200 probe response whose content-type is not the asset's type is the
 * SPA fallback serving HTML at a missing asset path — deploy skew that the
 * status code can't see (missing /assets/ paths historically returned 200
 * text/html with immutable caching). Absent header is treated as a match:
 * lack of a content-type is not evidence of skew, and a poisoned-cache
 * recovery must not be blocked by an exotic proxy stripping the header.
 */
export function contentTypeMatchesAsset(
  contentType: string | null,
  pathname: string,
): boolean {
  if (!contentType) {
    return true;
  }
  if (pathname.endsWith(".js")) {
    return /javascript|ecmascript/i.test(contentType);
  }
  if (pathname.endsWith(".css")) {
    return /\bcss\b/i.test(contentType);
  }
  return true;
}

/**
 * Retry ladder for failed dynamic chunk imports (Sentry APP-CFS).
 *
 * 1. Only definite chunk-load errors are retried (never lazyNamed's
 *    missing-export error).
 * 2. If the failed asset URL is extractable: probe it with
 *    fetch(cache:"reload"). A non-OK probe means deploy skew (the asset is
 *    gone) — rethrow immediately so the existing reload path handles it
 *    without further network amplification. An OK probe has also replaced a
 *    possibly-poisoned HTTP cache entry.
 * 3. Retry the factory once.
 * 4. Last resort for .js URLs: import with a ?ts= query param — a fresh
 *    module-map entry and a fresh fetch, bypassing any memoized failure.
 *
 * All failures rethrow the ORIGINAL error so error boundaries and the Sentry
 * chunk-load-error fingerprint behave exactly as before.
 *
 * `nativeImport` is an injectable seam: Vitest cannot resolve arbitrary URL
 * specifiers, so tests inject a mock.
 */
export async function importWithRetry<T>(
  factory: () => Promise<T>,
  nativeImport: (url: string) => Promise<unknown> = (url) =>
    import(/* @vite-ignore */ url),
): Promise<T> {
  try {
    return await factory();
  } catch (originalError) {
    if (
      !isDefiniteChunkLoadError(originalError) ||
      isMissingExportError(originalError)
    ) {
      throw originalError;
    }

    const url = extractAssetUrl(originalError);

    if (url) {
      let probe: Response;
      try {
        probe = await overwriteCachedAsset(url);
      } catch (probeError) {
        reportRetry("skew-probe", "failed", url.href, probeError);
        throw originalError;
      }
      if (!probe.ok) {
        // Deploy skew: the asset no longer exists on the current deploy.
        reportRetry("skew-probe", "skew-detected", url.href, originalError);
        throw markDeploySkew(originalError);
      }
      if (
        !contentTypeMatchesAsset(
          probe.headers.get("content-type"),
          url.pathname,
        )
      ) {
        // 200 but not the asset's type: skew the status code can't see.
        reportRetry("skew-probe", "skew-detected", url.href, originalError);
        throw markDeploySkew(originalError);
      }
    }

    const retryStage: RetryStage = url ? "cache-reload" : "plain-retry";
    try {
      const result = await factory();
      reportRetry(retryStage, "recovered", url?.href ?? null, originalError);
      return result;
    } catch (retryError) {
      if (
        !url ||
        !url.pathname.endsWith(".js") ||
        !isDefiniteChunkLoadError(retryError)
      ) {
        reportRetry(retryStage, "failed", url?.href ?? null, originalError);
        throw originalError;
      }

      try {
        const namespace = await nativeImport(`${url.href}?ts=${Date.now()}`);
        reportRetry("ts-param", "recovered", url.href, originalError);
        return namespace as T;
      } catch {
        reportRetry("ts-param", "failed", url.href, originalError);
        throw originalError;
      }
    }
  }
}
